// File: src/components/CareerInsightsCard.jsx
// Description: Career Insights Card component for dashboard integration

import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  useTheme,
  useMediaQuery,
  Stack,
  Divider,
  LinearProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  TrendingUp,
  Psychology,
  School,
  Work,
  ArrowForward,
  Timeline,
  Assessment
} from '@mui/icons-material';
import { useAI } from '../context/AIContext';
import { useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import AIService from '../services/AIService';

const CareerInsightsCard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useContext(AuthContext);
  const {
    careerInsights,
    careerGoals,
    addCareerInsight
  } = useAI();

  const [loading, setLoading] = useState(false);
  const [insightsLoaded, setInsightsLoaded] = useState(false);

  // Load career insights on mount - only if not already loaded
  useEffect(() => {
    if (!insightsLoaded && careerInsights.trajectory.length === 0 && user) {
      setInsightsLoaded(true);
    }
  }, [insightsLoaded, careerInsights.trajectory.length, user]);

  const getSkillPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return theme.palette.error.main;
      case 'medium': return theme.palette.warning.main;
      case 'low': return theme.palette.success.main;
      default: return theme.palette.text.secondary;
    }
  };

  const getSkillGapWidth = (gap) => {
    switch (gap) {
      case 'high': return 80;
      case 'medium': return 50;
      case 'low': return 20;
      default: return 0;
    }
  };

  return (
    <Card sx={{
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      border: `1px solid ${theme.palette.divider}`,
      transition: 'all 0.3s ease',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 12px 25px rgba(0, 0, 0, 0.1)',
      }
    }}>
      <CardContent sx={{ p: 2.5 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUp sx={{ color: theme.palette.success.main, fontSize: 24 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
              Career Insights
            </Typography>
          </Box>
          <IconButton
            size="small"
            href="/career-insights"
            sx={{ color: theme.palette.text.secondary }}
          >
            <ArrowForward />
          </IconButton>
        </Box>

        {/* Loading State */}
        {loading && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
              AI is analyzing your career...
            </Typography>
            <LinearProgress sx={{ mt: 1 }} />
          </Box>
        )}

        {/* Career Trajectory Preview */}
        {careerInsights.trajectory.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
              Career Trajectory
            </Typography>
            <Stack spacing={1}>
              {careerInsights.trajectory.slice(0, 2).map((step, index) => (
                <Box key={index} sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  p: 1,
                  bgcolor: theme.palette.background.paper,
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.divider}`
                }}>
                  <Work sx={{ color: theme.palette.primary.main, fontSize: 16 }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                      {typeof step.role === 'string' ? step.role : 'Unknown Role'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {typeof step.company === 'string' ? step.company : 'Unknown Company'} • {typeof step.year === 'string' || typeof step.year === 'number' ? step.year : 'Unknown Year'}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {/* Skill Gaps Preview */}
        {careerInsights.skillGaps.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
              Skill Development
            </Typography>
            <Stack spacing={1}>
              {careerInsights.skillGaps.slice(0, 3).map((skill, index) => (
                <Box key={index}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                      {typeof skill.name === 'string' ? skill.name : 'Unknown Skill'}
                    </Typography>
                    <Chip
                      label={typeof skill.priority === 'string' ? skill.priority : 'Unknown'}
                      size="small"
                      sx={{ 
                        fontSize: '0.65rem',
                        bgcolor: getSkillPriorityColor(skill.priority),
                        color: 'white'
                      }}
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={getSkillGapWidth(skill.gap)}
                    sx={{ height: 4, borderRadius: 2 }}
                  />
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {/* Next Steps Preview */}
        {careerInsights.nextSteps.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
              Next Steps
            </Typography>
            <Stack spacing={0.5}>
              {careerInsights.nextSteps.slice(0, 2).map((step, index) => (
                <Box key={index} sx={{ 
                  display: 'flex', 
                  alignItems: 'flex-start', 
                  gap: 1
                }}>
                  <Box sx={{ 
                    width: 6, 
                    height: 6, 
                    borderRadius: '50%', 
                    bgcolor: theme.palette.primary.main,
                    mt: 0.5,
                    flexShrink: 0
                  }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', lineHeight: 1.4, fontWeight: 500 }}>
                      {typeof step === 'string' ? step : 
                       typeof step.action === 'string' ? step.action : 'Unknown Action'}
                    </Typography>
                    {typeof step === 'object' && step.priority && (
                      <Chip 
                        label={`Priority: ${step.priority}`} 
                        size="small" 
                        sx={{ 
                          mt: 0.5,
                          fontSize: '0.6rem',
                          bgcolor: step.priority === 'high' ? theme.palette.error.main : 
                                   step.priority === 'medium' ? theme.palette.warning.main : 
                                   theme.palette.success.main,
                          color: 'white',
                          height: '16px'
                        }}
                      />
                    )}
                    {typeof step === 'object' && step.timeline && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Timeline: {step.timeline}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>
        )}

        {/* Quick Actions */}
        <Divider sx={{ my: 2 }} />
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Assessment />}
            sx={{ fontSize: '0.75rem' }}
            href="/career-insights"
          >
            Full Analysis
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<School />}
            sx={{ fontSize: '0.75rem' }}
            href="/career-insights"
          >
            Set Goals
          </Button>
        </Stack>

        {/* Stats */}
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
              {careerInsights.nextSteps.length}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Next Steps
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.warning.main }}>
              {careerInsights.skillGaps.filter(s => s.priority === 'high').length}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              High Priority
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CareerInsightsCard;
